import { StyleSheet } from "@react-pdf/renderer";
import theme from "../Theme/theme";
import { Font } from "@react-pdf/renderer";
import OpenSansBold from "../../../../assets/fonts/Open_Sans/OpenSans-Bold.ttf";
import OpenSansRegular from "../../../../assets/fonts/Open_Sans/OpenSans-Regular.ttf";

Font.register({
  family: "Open Sans",
  fonts: [
    { src: OpenSansBold, fontWeight: "bold" },
    { src: OpenSansRegular, fontWeight: "normal" },
  ],
});

//  Helper to convert hex to RGB
const hexToRgb = (hex) => {
  const cleanHex = hex.replace("#", "");
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};

// Helper to calculate brightness
const getTextColor = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  // relative luminance formula
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 186 ? "#000000" : "#ffffff"; // threshold
};

const blendWithWhite10 = (hex, opacity = 0.1) => {
  const { r, g, b } = hexToRgb(hex);
  const blended = {
    r: Math.round((1 - opacity) * 255 + opacity * r),
    g: Math.round((1 - opacity) * 255 + opacity * g),
    b: Math.round((1 - opacity) * 255 + opacity * b),
  };
  return `rgb(${blended.r}, ${blended.g}, ${blended.b})`;
};

const blendWithWhite = (hex, opacity = 0.1) => {
  const { r, g, b } = hexToRgb(hex);
  const blended = {
    r: Math.round((1 - opacity) * 255 + opacity * r),
    g: Math.round((1 - opacity) * 255 + opacity * g),
    b: Math.round((1 - opacity) * 255 + opacity * b),
  };
  return `rgb(${blended.r}, ${blended.g}, ${blended.b})`;
};

const ATRReportStyles = (primaryColor) =>
  // const textColor = getTextColor(primaryColor);
  // const highlightBg = blendWithWhite(primaryColor, 0.1);
  // const headerfooterBg = blendWithWhite10(primaryColor, 0.3);

  StyleSheet.create({
    page: {
      padding: 30,
      fontSize: 11,
      gap: 10,
      marginTop: 10,
      marginBottom: 10,
    },
    graphicWrapper: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
    graphicBox: {
      position: "absolute",
      top: "-102%",
      left: "-60%",
      width: "110%",
      height: "250%",
      backgroundColor: blendWithWhite(primaryColor, 0.6),
      transform: "rotate(45deg)",
    },
    graphicBox1: {
      position: "absolute",
      bottom: "40%",
      right: "-130%",
      width: "190%",
      height: "250%",
      backgroundColor: `#${primaryColor}`,
      transform: "rotate(35deg)",
    },
    graphicBox2: {
      position: "absolute",
      bottom: "73%",
      right: "-20%",
      width: "200%",
      height: "2%",
      backgroundColor: blendWithWhite10(primaryColor, 0.8),
      transform: "rotate(35deg)",
    },
    graphicBox3: {
      position: "absolute",
      bottom: "75.3%",
      right: "-20%",
      width: "200%",
      height: "2%",
      backgroundColor: blendWithWhite10(primaryColor, 0.3),
      transform: "rotate(35deg)",
    },
    bubblesWrapper: {
      position: "absolute",
      bottom: 40,
      right: 40,
      width: 320, // match outermost bubble width
      height: 320,
      justifyContent: "center",
      alignItems: "center",
      pointerEvents: "none",
    },

    bubbleOuter: {
      position: "absolute",
      right: -180,
      bottom: 10,
      width: 318,
      height: 318,
      borderRadius: 159,
      backgroundColor: `#${primaryColor}`,
      opacity: 0.11,
    },

    bubbleMiddle: {
      position: "absolute",
      right: 60,
      bottom: 140,
      width: 250,
      height: 250,
      borderRadius: 125,
      backgroundColor: `#${primaryColor}`,
      opacity: 0.11,
    },

    bubbleInner: {
      position: "absolute",
      right: 250,
      bottom: 120,
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: `#${primaryColor}`,
      opacity: 0.14,
    },
    header: {
      fontFamily: "Open Sans",
      fontSize: 18,
      textAlign: "center",
      fontWeight: "bold",
      letterSpacing: 1,
      color: `#${primaryColor}`,
      alignItems: "center",
      // flexGrow: 1,
    },
    row: {
      flexDirection: "row", // Align items horizontally
      justifyContent: "space-between", // Push content to edges
      alignItems: "center", // Align items vertically in the center
      alignContent: "center",
      marginBottom: 10,
    },
    column: {
      flexDirection: "column", // Align items horizontally
      justifyContent: "center", // Push content to edges
      alignItems: "center", // Align items vertically in the center
      alignContent: "center",
      marginBottom: 10,
    },
    headerContainer: {
      flexDirection: "column",
      justifyContent: "flex-start",

      // alignItems: "center",
      // padding: 20,
    },
    centeredContent: {
      flexDirection: "column",
      justifyContent: "flex-end",
      alignItems: "center",
      textAlign: "center",
      gap: 10,
      height: "100%",
      // marginTop: 450,
    },
    content: {
      marginBottom: 5,
      textAlign: "justify",
      fontSize: 10,
      lineHeight: 1.5,
      wrap: false,
    },
    subcontent: {
      fontFamily: "Open Sans",
      fontWeight: "bold",
      textAlign: "justify",
      fontSize: 10,
      lineHeight: 1.5,
      color: `#${primaryColor}`,
    },

    subheading: {
      fontFamily: "Open Sans",
      fontSize: 12,
      letterSpacing: 1,
      marginTop: 5,
      fontWeight: "bold",
      color: `#${primaryColor}`,
      lineHeight: 1.5,
    },
    text: { marginBottom: 5, color: theme.colors.Brand1PenBlue },
    alternateRow: {
      backgroundColor: blendWithWhite(primaryColor, 0.1),
    },
    table: {
      display: "table",
      width: "auto",
      borderStyle: "solid",
      borderWidth: 1,
      bordercolor: `#${primaryColor}`,
      marginBottom: 10,
      marginTop: 10,
    },
    tableRow: {
      flexDirection: "row",
      borderBottomStyle: "solid",
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.Brand4LavenderWeb,
      wrap: false,
    },
    tableHeader: {
      backgroundColor: `#${primaryColor}`,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      padding: 5,
      color: theme.colors.White,
      fontWeight: "bold",
      fontSize: 11,
    },
    tableCell: {
      padding: 5,
      fontSize: 10,
      flexGrow: 1,
    },
    questionCell: {
      flex: 4, // Wider for question
      padding: 5,
      fontSize: 10,
      borderRightColor: theme.colors.Brand4LavenderWeb,
      borderRightWidth: 1,
    },
    responseCell: {
      flex: 1, // Narrower for response
      padding: 5,
      fontSize: 10,
    },
    riskCell: {
      flex: 1,
      padding: 5,
      fontSize: 10,
      borderRightColor: theme.colors.Brand4LavenderWeb,
      borderRightWidth: 1,
    },
    descriptionCell: {
      flex: 3,
      padding: 5,
      fontSize: 10,
      wrap: false,
      minHeight: 50,
    },
    image: {
      maxWidth: 275,
      maxHeight: 215,
    },
  });

export default ATRReportStyles;
